import styled from 'styled-components';
import Box from '@mui/material/Box';
import { brand } from 'themes/brand';

export const reportColors = {
  background: brand.navy,
  surface: 'rgba(148, 163, 184, 0.08)',
  border: 'rgba(148, 163, 184, 0.25)',
  accent: brand.glow,
  foreground: '#e2e8f0',
  muted: '#94a3b8',
  success: '#34d399',
  warning: '#fbbf24',
  danger: '#f87171',
};

export const ReportPage = styled(Box)`
  background-color: ${reportColors.background};
  color: ${reportColors.foreground};
  border-radius: 16px;
  padding: 24px 28px;
  margin-bottom: 24px;
  box-shadow: 0 20px 45px rgba(2, 8, 23, 0.35);
`;

export const ReportHeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid ${reportColors.border};
  padding-bottom: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .report-label {
    font-size: 11px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${reportColors.muted};
  }

  .report-title {
    font-size: 11px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${reportColors.accent};
    font-weight: 700;
    text-align: right;
  }
`;

export const ReportPanel = styled(Box)<{ noPadding?: boolean }>`
  border: 1px solid ${reportColors.border};
  background-color: ${reportColors.surface};
  border-radius: 14px;
  padding: ${({ noPadding }) => (noPadding ? '0' : '18px 20px')};
  height: 100%;
`;

export const PanelTitle = styled.h3`
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${reportColors.accent};
  font-weight: 700;
  margin: 0 0 14px 0;
`;

export const DataRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  font-size: 15px;
  margin-bottom: 10px;

  .label {
    color: ${reportColors.muted};
    font-size: 13px;
    width: 130px;
    flex-shrink: 0;
  }

  .value {
    font-weight: 500;
  }

  .value.strong {
    font-weight: 700;
    color: ${reportColors.accent};
  }
`;

export const BarTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`;

export const BarFill = styled.div<{
  value: number;
  tone: 'success' | 'warning' | 'danger';
}>`
  height: 100%;
  border-radius: 999px;
  width: ${({ value }) => Math.max(3, Math.min(100, value))}%;
  background-color: ${({ tone }) =>
    tone === 'success'
      ? reportColors.success
      : tone === 'warning'
      ? reportColors.warning
      : reportColors.danger};
`;

export const BarRow = styled.div`
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }

  .bar-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .bar-value {
    font-size: 19px;
    font-weight: 700;
  }

  .bar-tag {
    font-size: 11px;
    color: ${reportColors.muted};
    margin-top: 4px;
  }
`;

export const ExecutiveScore = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  .score {
    font-size: 56px;
    font-weight: 900;
    color: ${reportColors.success};
    line-height: 1;
  }

  .score-caption {
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: ${reportColors.muted};
    margin-bottom: 4px;
  }

  .decision-label {
    font-size: 21px;
    font-weight: 700;
  }

  .validated {
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${reportColors.success};
    font-weight: 600;
    margin-top: 4px;

    svg {
      font-size: 16px;
    }
  }
`;

export const MatchTrack = styled.div`
  padding-top: 24px;
  position: relative;

  .track-labels,
  .track-scale {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: ${reportColors.muted};
  }

  .track-labels {
    margin-bottom: 4px;
  }

  .track-scale {
    margin-top: 4px;
  }

  .track-bar {
    position: relative;
    height: 12px;
    border-radius: 999px;
    overflow: hidden;
    display: flex;
  }

  .track-bar > span {
    flex: 1;
  }

  .track-marker {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 0 2px ${reportColors.background};
  }

  .track-score {
    position: absolute;
    top: -6px;
    transform: translateX(-50%);
    font-weight: 700;
    color: ${reportColors.success};
    font-size: 14px;
  }
`;

export const SummaryText = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${reportColors.foreground};
  opacity: 0.9;
  margin: 0;
`;

export const RadarLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  font-size: 12px;
  margin-top: 8px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  i {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    display: inline-block;
  }
`;

export const ComparisonTableWrapper = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 15px;
  }

  thead th {
    text-align: left;
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: ${reportColors.accent};
    border-bottom: 1px solid ${reportColors.border};
    padding: 8px 14px 8px 0;
  }

  tbody td {
    padding: 10px 14px 10px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

export const EmptyState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 0;

  svg {
    font-size: 32px;
    color: ${reportColors.accent};
    margin-bottom: 12px;
  }

  .empty-title {
    font-size: 17px;
    font-weight: 700;
  }

  .empty-description {
    font-size: 14px;
    color: ${reportColors.muted};
    margin-top: 8px;
    max-width: 260px;
  }
`;

export const PdiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const PdiCard = styled.div`
  border: 1px solid ${reportColors.border};
  border-radius: 12px;
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.02);

  .priority {
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: ${reportColors.accent};
    font-weight: 700;
    margin-bottom: 8px;
  }

  .focus {
    font-size: 15px;
    margin-bottom: 12px;

    .focus-label {
      font-size: 10px;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: ${reportColors.muted};
    }

    .focus-value {
      font-weight: 700;
      margin-left: 4px;
    }
  }

  .section-label {
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: ${reportColors.muted};
    font-weight: 600;
    margin-top: 12px;
  }

  .section-label.accent {
    color: ${reportColors.accent};
  }

  p {
    font-size: 14px;
    margin: 6px 0 0 0;
    line-height: 1.5;
  }

  ul {
    margin: 6px 0 0 0;
    padding-left: 18px;
    font-size: 14px;

    li {
      margin-bottom: 4px;
    }

    li::marker {
      color: ${reportColors.accent};
    }
  }
`;
